// Implements: F-VER-004, F-UIX-009, F-UIX-010
/**
 * One version of a mockup, and whether it is the one the mockup points at.
 *
 * The version is its own query; the pointer comes from the mockup under the
 * SHARED `mockupDetailKey`, the very query the scope selector and the tree
 * read, so promoting a version refreshes all of them with one invalidation.
 */

import { useQuery } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import { fetchMockupDetail } from "../../../shared/api/mockupDetail";
import { mockupDetailKey, versionDetailKey } from "../../../shared/api/queryKeys";
import type { MockupDetailView, VersionDetailView } from "../../../types/api";
import { fetchVersion } from "../api/versionsApi";

export interface VersionDetailData {
  readonly version: VersionDetailView;
  readonly isCurrent: boolean;
  /** The owner and an editor may change what the mockup continues from; a viewer may not. */
  readonly canEdit: boolean;
}

export type UseVersionDetailResult =
  | { readonly status: "pending" }
  | { readonly status: "error"; readonly error: ApiError; readonly refetch: () => void }
  | { readonly status: "success"; readonly data: VersionDetailData };

export function useVersionDetail(mockupId: string, versionId: string): UseVersionDetailResult {
  const version = useQuery<VersionDetailView, ApiError>({
    queryKey: versionDetailKey(mockupId, versionId),
    queryFn: () => fetchVersion(mockupId, versionId),
  });
  const mockup = useQuery<MockupDetailView, ApiError>({
    queryKey: mockupDetailKey(mockupId),
    queryFn: () => fetchMockupDetail(mockupId),
  });

  const failure = version.error ?? mockup.error;
  if (failure !== null) {
    return {
      status: "error",
      error: failure,
      refetch: () => {
        void version.refetch();
        void mockup.refetch();
      },
    };
  }
  if (version.data === undefined || mockup.data === undefined) return { status: "pending" };
  return {
    status: "success",
    data: {
      version: version.data,
      isCurrent: mockup.data.current_version_id === versionId,
      canEdit: mockup.data.your_role === "owner" || mockup.data.your_role === "editor",
    },
  };
}
